import React, { useState, useEffect, useMemo } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    Input as AntInput,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';

import { fetcProductData } from '../../../../redux/slice/masterSlice';
import {
    BrandNames,
    UnitType
} from '../../../../types/CreateProductType';
import BrandNameModel from '../../masters/brand-names/BrandNameModel';
import CategoryModal from '../../Category/CategoryModal';



interface ProductModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        product_id?: number;
        product_name?: string;
        product_code?: string;       // added
        product_price?: number;      // added
        product_unit?: number;      // added
        product_category?: number;      // added
        category_id?: number;      // added
        brand_id?: number;      // added
        product_brand_name?: number;      // added
    } | null;
    isEditMode?: boolean;
}
interface Categories {
    category_id: number;
    category_name: string;
    category_status: number;
    category_short_code: string;
}
type FetchKey =
    | 'categories'
    | 'brands'
const ProductModal: React.FC<ProductModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);
    const [brandnames, setBrandNames] = useState<BrandNames[]>([]);
    const [categories, setCategories] = useState<Categories[]>([]);
    const [selectedBrandNames, setSelectedBrandNames] = useState<number | null>(null);
    const [isBrandNameModalVisible, setIsBrandNameModalVisible] = useState(false);

    const [selectedCategories, setSelectedCategories] = useState<number | null>(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);

    const { unittypes: reduxUnitTypes } = useAppSelector((state) => state.master);
    const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
    const effectiveUnitTypes = (unitTypes && unitTypes.length > 0 ? unitTypes : (reduxUnitTypes || [])) as UnitType[];

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };

    const loadunits = async () => {
        try {
            const res = await apiRequest({ method: "get", url: "unit-types?status=1" });
            if (Array.isArray(res)) {
                setUnitTypes(res);
            }
        } catch (e) {
            console.error("Failed to load units", e);
        }
    };
    // Handle form submit
    const handleAddProduct = async (values: any) => {
        try {
            const dataVal = {
                product_name: values.product_name,
                product_code: values.product_code,
                product_unit: values.product_unit,
                product_category: values.product_category,
                product_brand_name: values.product_brand_name,
                product_price: parseFloat(values.product_price),  // convert to number
                ...(initialValues?.product_id && { product_id: initialValues.product_id }),
            };
            const url = isEditMode ? 'edit-handicraft-product' : 'add-handicraft-product';
            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });

            const selectedCat = categories.find((cat) => cat.category_id === values.product_category);
            onCreate({
                ...data,
                category_name: selectedCat ? selectedCat.category_name : '',
                category_id: values.product_category,
            });
            showSuccess(isEditMode ? 'Product updated successfully!' : 'Product created successfully!');
            // form.resetFields();

            if (isModalClose) {
                onCancel();
            }

            dispatch(fetcProductData());
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} product`);
        }
    };

    // Brand name
    const handleBrandNameCreated = (newBrandName: BrandNames) => {
        fetchAllDataSequentially(['brands']);
        setSelectedBrandNames(newBrandName.brand_id);
        setIsBrandNameModalVisible(false);
    };
    const fetchAllDataSequentially = async (
        keys: FetchKey[] = ['categories', 'brands']
    ) => {
        try {
            if (keys.includes('categories')) {
                setCategories(await apiRequest({ method: 'get', url: 'category?status=1' }));
            }

            if (keys.includes('brands')) {
                setBrandNames(await apiRequest({ method: 'get', url: 'brand?status=1' }));
            }



        } catch (error) {
            showError('Failed to fetch some data');
            console.error(error);
        }
    };
    const debouncedFetchAllData = useMemo(
        () =>
            debounce((keys?: FetchKey[]) => {
                fetchAllDataSequentially(keys);
            }, 500),
        []
    );

    // Category
    const handleCategoryCreated = (newCategory: Categories) => {
        fetchAllDataSequentially(['categories']);
        setSelectedCategories(newCategory.category_id);
        setIsCategoryModalVisible(false);
    };
    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                product_name: initialValues.product_name,
                product_code: initialValues.product_code,
                product_unit:
                    initialValues.product_unit !== undefined && initialValues.product_unit !== null
                        ? Number(initialValues.product_unit)
                        : undefined,
                product_brand_name: initialValues.brand_id ? Number(initialValues.brand_id) : undefined,
                product_category: initialValues.category_id ? Number(initialValues.category_id) : undefined,
                product_price: initialValues.product_price?.toString(), // convert number to string for Input
            });
        } else if (visible) {
            form.resetFields();
        }

        loadunits();
    }, [visible, isEditMode, initialValues, form]);


    useEffect(() => {
        loadunits();
        debouncedFetchAllData();
    }, []);

    return (
        <Modal
            title={isEditMode ? 'Edit Product' : 'Add New Product'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddProduct}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="product_name"
                            label={
                                <span>
                                    Product Name&nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>(Max 255 characters)</span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter the Product Name' },
                                { max: 255, message: 'Maximum 255 characters allowed' },
                            ]}
                        >
                            <Input showCount maxLength={255} placeholder="e.g., Product" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="product_code"
                            label="Product Code"
                            rules={[
                                { required: true, message: 'Please enter the Product Code' },
                                { max: 50, message: 'Maximum 50 characters allowed' },
                            ]}
                        >
                            <Input maxLength={50} placeholder="e.g., PROD1234" />
                        </Form.Item>
                    </Col>
                    {/* Brand */}
                    <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            Brand Name
                        </div>

                        <Form.Item name="product_brand_name">
                            <AntInput.Group compact style={{ display: 'flex', flexWrap: 'nowrap', width: '100%' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Select
                                        showSearch
                                        placeholder="Select Brand Name"
                                        style={{ width: '100%' }}
                                        onChange={(value) => {
                                            const selected = brandnames.find((bn) => bn.brand_id === value);
                                            if (selected) {
                                                form.setFieldsValue({ product_brand_name: selected.brand_id });
                                            } else {
                                                form.setFieldsValue({ product_brand_name: '' });
                                            } setSelectedBrandNames(value)
                                        }}
                                        options={[
                                            { label: 'Select Brand Name', value: null },
                                            ...brandnames.map((brand) => ({
                                                label: brand.brand_name,
                                                value: brand.brand_id,
                                            })),
                                        ]}
                                        allowClear
                                        filterOption={(input, option) =>
                                            (option?.label as string).toLowerCase().includes(input.toLowerCase())
                                        }
                                        dropdownMatchSelectWidth={false}
                                        value={form.getFieldValue('product_brand_name')}
                                    />
                                </div>
                                <Button
                                    style={{ padding: 0, flexShrink: 0 }}
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsBrandNameModalVisible(true)}
                                />
                            </AntInput.Group>
                        </Form.Item>

                        <BrandNameModel
                            visible={isBrandNameModalVisible}
                            onCancel={() => setIsBrandNameModalVisible(false)}
                            onCreate={handleBrandNameCreated}
                            isEditMode={false}
                            initialValues={null}
                        />
                    </Col>
                    {/* category */}
                    <Col xs={24} sm={12} md={8} >
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            Category
                        </div>

                        <Form.Item name="product_category"
                            rules={[{ required: true, message: 'Please select a category!' }]}>
                            <AntInput.Group compact style={{ display: 'flex', flexWrap: 'nowrap', width: '100%' }}>
                                <Select
                                    showSearch
                                    placeholder="Select Category"
                                    onChange={(value) => {
                                        const selected = categories.find((cat) => cat.category_id === value);
                                        console.log("from catogey sel", selected)
                                        form.setFieldsValue({
                                            product_category: selected ? selected.category_id : '',
                                            product_code: selected ? selected.category_short_code : '',
                                        });
                                        setSelectedCategoryId(selected ? selected.category_short_code : '');
                                    }}
                                    options={[
                                        { label: 'Select Category', value: undefined },
                                        ...categories.map((cat) => ({
                                            label: cat.category_name,
                                            value: cat.category_id,
                                        })),
                                    ]}
                                    allowClear
                                    filterOption={(input, option) =>
                                        (option?.label as string).toLowerCase().includes(input.toLowerCase())
                                    }
                                    style={{ flex: 1, minWidth: 0 }}
                                    value={form.getFieldValue('product_category')}
                                />
                                <Button
                                    style={{ padding: 0 }}
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsCategoryModalVisible(true)}
                                />
                            </AntInput.Group>
                        </Form.Item>

                        {/* Reusable GST Modal */}
                        <CategoryModal
                            visible={isCategoryModalVisible}
                            onCancel={() => setIsCategoryModalVisible(false)}
                            onCreate={handleCategoryCreated}
                            isEditMode={false}
                            initialValues={null}
                        />
                    </Col>
                    {/* Unit Type */}
                    <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            Unit Type
                        </div>

                        <Form.Item
                            name="product_unit"
                            rules={[{ required: true, message: 'Please select a unit type!' }]}
                        >
                            <Select
                                placeholder="Select Unit"
                                style={{ width: "100%" }}
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                options={effectiveUnitTypes.map((unit: any) => ({
                                    label: unit.ut_name,
                                    value: Number(unit.ut_id),
                                }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="product_price"
                            label="Product Price"
                            rules={[
                                {
                                    pattern: /^\d+(\.\d{1,4})?$/,
                                    message: 'Enter a valid price (up to 4 decimals)',
                                },
                            ]}
                        >
                            <Input
                                placeholder="e.g., 199.9999"
                                onKeyDown={(e) => {
                                    if (
                                        e.key === " " ||
                                        e.key === "-" ||
                                        e.key === "+"
                                    ) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Product
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ProductModal;
